import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ConnectedFacility,
  ProviderDetail,
  QuestionnaireService,
  ProviderService,
} from '../../../../../js/api';
import { providerQueryKeys } from '../../../../../js/app/query-keys-factories/providers';
import { Dialog, DialogTitle, DialogBody, DialogCloseButton } from '../../../components/dialog';
import { DrawerSectionHeader, DrawerField } from '../../../components/drawer-components';
import {
  QUESTION_DEFINITIONS,
  ProviderQuestionnaireOrgView,
} from '../../../components/provider-questionnaire-org-view';
import { TooltippedText } from '../../../components/tooltipped-text';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { formatApiDate } from '../../../utils/date';

type PresentationInfoTabProps = {
  connection: ConnectedFacility;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function PresentationInfoTab({ connection }: PresentationInfoTabProps) {
  const providerId = connection.provider?.id;

  const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(false);

  const { data: providerDetail } = useQuery<ProviderDetail>({
    queryKey: ['provider', providerId],
    queryFn: () => ProviderService.providerRetrieve({ providerId: providerId! }),
    enabled: !!providerId,
  });

  const {
    data: questionnaireData,
    isLoading: questionnaireIsLoading,
    isError: questionnaireIsError,
  } = useQuery({
    queryKey: providerQueryKeys.questionnaire(providerId),
    queryFn: () => QuestionnaireService.questionnaireRetrieve({ providerId }),
    enabled: !!providerId,
  });

  const hasData =
    questionnaireData?.data &&
    typeof questionnaireData?.data === 'object' &&
    Object.keys(questionnaireData?.data).length > 0;

  // Normalize questionnaire data for easier processing
  const qData = questionnaireData?.data as
    | Record<string, { value?: boolean | null; comment?: string | null }>
    | undefined;

  const yesItems = QUESTION_DEFINITIONS.filter(({ key }) => qData?.[key]?.value === true).map(
    ({ key, question }) => ({ key, question, comment: qData?.[key]?.comment || '' })
  );

  const allNo = !!hasData && QUESTION_DEFINITIONS.every(({ key }) => qData?.[key]?.value === false);

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps([
    'BoardCertificationType',
    'CertificationType',
    'LicenseState',
  ]);

  const boardCertMap = codeDefinitionMaps?.BoardCertificationType || {};
  const stateMap = codeDefinitionMaps?.LicenseState || {};

  // Find active license matching facility state
  const facilityState = connection.facility?.state;
  const activeLicense = providerDetail?.licenses?.find(
    (license) =>
      facilityState &&
      String(license.state) === String(facilityState) &&
      license.status === 'ACTIVE'
  );

  return (
    <div className="tw:space-y-4">
      {/* Provider Profile Link */}
      <section>
        <div className="tw:mt-2">
          <a
            href={`/app/healthcare-entity/providers/${providerId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:hover:underline tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
          >
            <i className="far fa-arrow-up-right-from-square tw:mr-2 fa-fw" />
            View Provider Profile
          </a>
        </div>
      </section>

      {/* CV */}
      {providerDetail && (
        <section>
          <div className="tw:mt-2">
            {providerDetail.cv ? (
              <a
                href={providerDetail.cv}
                target="_blank"
                rel="noopener noreferrer"
                className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:hover:underline tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
              >
                <i className="fas fa-download tw:mr-2 fa-fw" />
                Download CV
              </a>
            ) : (
              <span className="tw:text-gray-500 tw:dark:text-gray-400">
                <i className="fas fa-file-lines tw:mr-2 fa-fw" /> No CV available
              </span>
            )}
          </div>
        </section>
      )}

      {/* Malpractice History (inline questionnaire summary) */}
      {providerId && (
        <section>
          <DrawerSectionHeader
            text="Malpractice & Other History"
            icon={
              <i className="fas fa-clipboard-question tw:text-brand-green-0 tw:dark:text-brand-green-1 fa-fw" />
            }
            rightElement={
              <div className="tw:flex tw:items-center tw:gap-2">
                <TooltippedText
                  placement="left"
                  tooltipContent={
                    <>
                      <div>Any questions with a YES response will be displayed below.</div>
                      <div className="tw:max-w-xs">
                        Click View to see all of the questions asked related to Licensure,
                        Professional Liability & Claims History, Background, and Job Performance.
                      </div>
                    </>
                  }
                >
                  <i className="far fa-circle-info tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:text-sm" />
                </TooltippedText>
                <button
                  type="button"
                  onClick={() => setIsQuestionnaireOpen(true)}
                  className="tw:text-xs tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:underline"
                >
                  View
                </button>
              </div>
            }
          />
          <div className="tw:mt-2">
            {questionnaireIsLoading ? (
              <div className="tw:flex tw:items-center tw:py-2 tw:text-sm tw:text-zinc-500">
                <div
                  className="tw:animate-spin tw:rounded-full tw:h-4 tw:w-4 tw:border-2 tw:border-zinc-300 tw:border-t-zinc-600 tw:mr-2"
                  aria-label="Loading questionnaire"
                />
                Loading malpractice history...
              </div>
            ) : questionnaireIsError ? (
              <div className="tw:text-sm tw:text-zinc-600 tw:dark:text-zinc-400 tw:ml-5.75">
                <i className="fas fa-triangle-exclamation tw:text-stoplight-warning tw:mr-1" />
                No response yet from provider
              </div>
            ) : !hasData ? (
              <div className="tw:text-sm tw:text-zinc-700 tw:dark:text-zinc-300 tw:ml-5.75">
                <i className="fas fa-triangle-exclamation tw:text-stoplight-warning tw:mr-1" />
                No response yet from provider
              </div>
            ) : allNo ? (
              <div className="tw:text-sm tw:text-zinc-700 tw:dark:text-zinc-300 tw:ml-5.75">
                <i className="fas fa-check-circle tw:text-stoplight-success" /> No Malpractice
                History or other Issues Reported
              </div>
            ) : (
              <div className="tw:space-y-6">
                {yesItems.map(({ key, question, comment }) => (
                  <div key={key} className="tw:space-y-1">
                    <div className="tw:ml-5.75 tw:text-zinc-700 tw:dark:text-zinc-300 tw:text-sm">
                      <i className="fas fa-x tw:text-stoplight-danger tw:mr-1" />
                      {question}
                    </div>
                    <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-1 tw:text-sm">
                      <DrawerField label="Answer" value="YES" />
                    </div>
                    {comment ? (
                      <div className="tw:mt-2 tw:ml-5.75">
                        <div className="tw:text-zinc-400 tw:text-sm">Comment:</div>
                        <div className="tw:text-zinc-700 tw:dark:text-zinc-300 tw:whitespace-pre-line tw:rounded tw:bg-zinc-50 tw:dark:bg-zinc-800/50 tw:p-3 tw:mt-1">
                          {comment}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Full Questionnaire Modal */}
      {providerId && (
        <Dialog open={isQuestionnaireOpen} onClose={() => setIsQuestionnaireOpen(false)} size="3xl">
          <div className="tw:flex tw:items-center tw:justify-between">
            <DialogTitle>Provider Questionnaire</DialogTitle>
            <DialogCloseButton onClick={() => setIsQuestionnaireOpen(false)} />
          </div>
          <DialogBody>
            {/* Render the full questionnaire view */}
            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
            <div className="tw:space-y-4">
              <p className="tw:text-sm tw:text-zinc-600 tw:dark:text-zinc-400">
                The sections below include Licensure, Professional Liability & Claims History,
                Background, and Job Performance.
              </p>
              {/* Reuse the existing full questionnaire component */}
              {/* We know providerId exists due to the surrounding conditional */}
              <ProviderQuestionnaireOrgView providerId={providerId!} />
            </div>
          </DialogBody>
        </Dialog>
      )}

      {/* Active State License */}
      <section>
        <DrawerSectionHeader
          text="State License"
          icon={
            <i className="fas fa-id-card tw:text-brand-green-0 tw:dark:text-brand-green-1 fa-fw" />
          }
        />
        <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
          <DrawerField label="IMLC" value={providerDetail?.has_imlc ? 'Yes' : 'No'} />
          {activeLicense && (
            <>
              <DrawerField
                label="State"
                value={stateMap[activeLicense.state] || activeLicense.state}
              />
              <DrawerField label="Number" value={activeLicense.number || '--'} />
              <DrawerField
                label="Expiration Date"
                value={
                  activeLicense.expiration_date
                    ? formatApiDate(activeLicense.expiration_date, { dateFormat: 'MM/dd/yyyy' })
                    : '--'
                }
              />
            </>
          )}
        </div>
      </section>

      {/* Board Certifications */}
      {providerDetail?.board_certifications && providerDetail.board_certifications.length > 0 && (
        <section>
          <DrawerSectionHeader
            text="Board Certifications"
            icon={
              <i className="fas fa-certificate tw:text-brand-green-0 tw:dark:text-brand-green-1 fa-fw" />
            }
          />
          <div className="tw:space-y-2 tw:text-sm">
            {providerDetail.board_certifications.map((cert, index) => {
              const certType = boardCertMap[cert.type] || cert.type;
              const eligibleText = cert.board_eligible ? ' - Board Eligible' : '';
              return (
                <div key={cert.id || index} className="tw:flex tw:items-center tw:ml-5.75">
                  <span>
                    {certType} {eligibleText}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Life Support Certifications */}
      {providerDetail?.certifications && providerDetail.certifications.length > 0 && (
        <section>
          <DrawerSectionHeader
            text="Life Support Certifications"
            icon={
              <i className="fas fa-heartbeat tw:text-brand-green-0 tw:dark:text-brand-green-1 fa-fw" />
            }
          />
          <div className="tw:space-y-2 tw:text-sm">
            {providerDetail.certifications.map((cert, index) => {
              const expirationText = cert.expiration_date
                ? ` - Expires: ${formatApiDate(cert.expiration_date, { dateFormat: 'MM/dd/yyyy' })}`
                : '';
              return (
                <div key={cert.id || index} className="tw:flex tw:items-center tw:ml-5.75">
                  <span>
                    {cert.type} {expirationText}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Questionnaire modal removed in favor of inline summary */}
    </div>
  );
}
